import { useRef, useState } from 'react';
import { Upload, ShieldCheck, Loader2, X, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const absoluteImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

/**
 * ImageUploader
 * Accepts a `value` object of shape { url, alt } and calls onChange({ url, alt }).
 * Supports either uploading a file (EXIF/GPS auto-stripped server-side) OR pasting a URL.
 */
export const ImageUploader = ({ value, onChange, label = 'Image' }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // { gps_removed, size, width, height }
  const [mode, setMode] = useState('upload'); // 'upload' | 'url'
  const urlValue = value?.url || '';
  const altValue = value?.alt || '';

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8 MB).');
      return;
    }
    setUploading(true);
    setStatus(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      onChange({ url: d.url, alt: altValue });
      setStatus(d);
      toast.success(d.gps_removed ? 'Uploaded. EXIF GPS removed.' : 'Uploaded. Metadata stripped.');
    } catch (err) {
      const message = err?.response?.data?.detail || 'Upload failed.';
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const clear = () => {
    onChange({ url: '', alt: '' });
    setStatus(null);
  };

  const preview = absoluteImageUrl(urlValue);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="kicker">{label}</Label>
        <div className="inline-flex rounded-lg border border-[hsl(var(--border))] p-0.5 bg-[hsl(var(--secondary))]">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              mode === 'upload' ? 'bg-[hsl(var(--card))] shadow-sm' : 'text-[hsl(var(--muted-foreground))]'
            }`}
          >
            <Upload size={11} className="inline mr-1" /> Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              mode === 'url' ? 'bg-[hsl(var(--card))] shadow-sm' : 'text-[hsl(var(--muted-foreground))]'
            }`}
          >
            <LinkIcon size={11} className="inline mr-1" /> URL
          </button>
        </div>
      </div>

      {mode === 'upload' ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="rounded-xl border-2 border-dashed border-[hsl(var(--border))] bg-[hsl(var(--secondary)/0.4)] p-6 text-center"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <div className="flex flex-col items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            </div>
            <p className="text-sm font-medium">{uploading ? 'Uploading & stripping metadata…' : 'Drop an image or choose a file'}</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] max-w-sm">
              JPEG, PNG, or WebP up to 8 MB. All EXIF data (including GPS location) is removed automatically before storage.
            </p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="mt-1"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              Choose file
            </Button>
          </div>
        </div>
      ) : (
        <Input
          value={urlValue}
          onChange={(e) => onChange({ url: e.target.value, alt: altValue })}
          placeholder="https://… (external image URL)"
        />
      )}

      {status?.gps_removed && (
        <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--success,142_50%_35%))]/40 bg-[hsl(142_50%_35%/0.08)] px-3 py-2 text-xs text-[hsl(142_50%_25%)]">
          <ShieldCheck size={14} />
          <span>EXIF GPS location was found in this image and has been removed before storage.</span>
        </div>
      )}
      {status && !status.gps_removed && status.size && (
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          Metadata stripped. Stored image: {status.width}×{status.height}, {(status.size / 1024).toFixed(0)} KB.
        </p>
      )}

      {urlValue && (
        <div className="space-y-2">
          <div className="relative rounded-lg overflow-hidden border border-[hsl(var(--border))]">
            <img src={preview} alt={altValue || label} className="h-40 w-full object-cover" />
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
              aria-label="Remove image"
            >
              <X size={14} />
            </button>
          </div>
          <Input
            placeholder="Alt text — describe the image for accessibility"
            value={altValue}
            onChange={(e) => onChange({ url: urlValue, alt: e.target.value })}
          />
        </div>
      )}
    </div>
  );
};
